const KEY_NS = 'tmp.v1';
const API_KEY_KEY = `${KEY_NS}.apiKey`;
const HISTORY_KEY = `${KEY_NS}.history`;
const DEFAULT_HISTORY_CAP = 6;

export function getApiKey(): string {
  return getStorage()?.getItem(API_KEY_KEY) ?? '';
}

export function setApiKey(k: string): void {
  getStorage()?.setItem(API_KEY_KEY, k);
}

export function clearApiKey(): void {
  getStorage()?.removeItem(API_KEY_KEY);
}

export type StoredJourney = {
  id: string;
  location: string;
  prompt: string;
  years: number[];
  frames: Array<{ year: number; b64: string }>;
  createdAt: number;
};

export function getHistory(): StoredJourney[] {
  const raw = getStorage()?.getItem(HISTORY_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isStoredJourney) : [];
  } catch {
    return [];
  }
}

export function pushHistory(j: StoredJourney, cap = DEFAULT_HISTORY_CAP): StoredJourney[] {
  let next = [j, ...getHistory()].slice(0, Math.max(0, cap));

  for (;;) {
    try {
      writeHistory(next);
      return next;
    } catch (error) {
      if (!isQuotaExceededError(error)) {
        throw error;
      }

      if (next.length === 0) {
        clearHistory();
        return [];
      }

      next = next.slice(0, Math.max(0, next.length - 1));
    }
  }
}

export function clearHistory(): void {
  getStorage()?.removeItem(HISTORY_KEY);
}

function writeHistory(history: StoredJourney[]): void {
  getStorage()?.setItem(HISTORY_KEY, JSON.stringify(history));
}

function isStoredJourney(value: unknown): value is StoredJourney {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === 'string' &&
    typeof value.location === 'string' &&
    typeof value.prompt === 'string' &&
    Array.isArray(value.years) &&
    value.years.every((year) => typeof year === 'number') &&
    Array.isArray(value.frames) &&
    value.frames.every(
      (frame) => isRecord(frame) && typeof frame.year === 'number' && typeof frame.b64 === 'string',
    ) &&
    typeof value.createdAt === 'number'
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isQuotaExceededError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'QuotaExceededError';
}

function getStorage(): Storage | undefined {
  const candidate =
    typeof window !== 'undefined' && window.localStorage
      ? window.localStorage
      : typeof globalThis !== 'undefined' && 'localStorage' in globalThis
        ? globalThis.localStorage
        : undefined;

  if (
    candidate &&
    typeof candidate.getItem === 'function' &&
    typeof candidate.setItem === 'function' &&
    typeof candidate.removeItem === 'function'
  ) {
    return candidate;
  }

  return undefined;
}
